import { createSlice } from "@reduxjs/toolkit"
import type { PayloadAction } from "@reduxjs/toolkit"
import type { CheckoutPriceChange } from "@/domain/cart/checkout-price-change"
import { isPriceChangeAcknowledgementRequired } from "@/domain/cart/checkout-price-change"
import { DEFAULT_CHECKOUT_PAYMENT_MODE, PAYMENT_MODE, type PaymentMode } from "@/domain/payment/payment-mode"
import { DEFAULT_FULFILMENT, type Fulfilment } from "@/domain/checkout/fulfilment"
import type { Dispatch } from "redux"
import {
  clearAllCheckoutFulfilmentSessions,
  clearCheckoutFulfilmentSession,
  readCheckoutFulfilmentSession,
  writeCheckoutFulfilmentSession,
} from "@/domain/checkout/fulfilment-session"

export type CheckoutPhase = "cart" | "preview" | "billing" | "payment" | "final_review"

/**
 * UX-only state for checkout progress and messaging.
 * Does not affect business logic.
 */
export interface CheckoutUxState {
  phase: CheckoutPhase
  paymentMode: PaymentMode
  /**
   * Customer's fulfilment selection. Only a selection: the applied method also needs the
   * restaurant's `website_pickup_enabled` (see `resolveEffectiveFulfilment`). Changing it never
   * touches the checkout address selection, so Delivery's address is restored on switching back.
   */
  fulfilment: Fulfilment
  /**
   * Customer order instructions for the current checkout session, sent as the
   * existing public `instructions` field. Not persisted — cleared on a
   * successful order, a deliberate cart clear, and a committed tenant change.
   */
  instructions: string
  /**
   * Price-drift signature for this checkout session. Null when no drift was
   * detected. Not sent to the backend. Cleared with the rest of the session.
   */
  priceChangeSignature: string | null
  /** Signature the customer confirmed. A newer signature does not match. */
  priceChangeAcknowledgedSignature: string | null
  /** Item-level old → current prices shown with the acknowledgement. */
  priceChangeNotice: CheckoutPriceChange[] | null
}

const initialState: CheckoutUxState = {
  phase: "cart",
  paymentMode: DEFAULT_CHECKOUT_PAYMENT_MODE,
  fulfilment: DEFAULT_FULFILMENT,
  instructions: "",
  priceChangeSignature: null,
  priceChangeAcknowledgedSignature: null,
  priceChangeNotice: null,
}

export const checkoutUxSlice = createSlice({
  name: "checkoutUx",
  initialState,
  reducers: {
    setCheckoutPhase: (state, action: PayloadAction<CheckoutPhase>) => {
      state.phase = action.payload
    },
    setCheckoutPaymentMode: (state, action: PayloadAction<PaymentMode>) => {
      state.paymentMode = action.payload
    },
    /** Pickup is prepaid only, so selecting it moves payment to ONLINE. Delivery keeps the mode. */
    setCheckoutFulfilment: (state, action: PayloadAction<Fulfilment>) => {
      state.fulfilment = action.payload
      if (action.payload === "PICKUP") {
        state.paymentMode = PAYMENT_MODE.ONLINE
      }
    },
    resetCheckoutFulfilment: (state) => {
      state.fulfilment = DEFAULT_FULFILMENT
    },
    setCheckoutInstructions: (state, action: PayloadAction<string>) => {
      state.instructions = action.payload
    },
    clearCheckoutInstructions: (state) => {
      state.instructions = ""
    },
    /**
     * Records a price drift for this session. The same signature does not
     * clear an acknowledgement the customer already gave.
     */
    noteCheckoutPriceChange: (
      state,
      action: PayloadAction<{ signature: string; changes: CheckoutPriceChange[] }>
    ) => {
      const signature = action.payload.signature
      if (!signature || state.priceChangeSignature === signature) return
      state.priceChangeSignature = signature
      state.priceChangeNotice = action.payload.changes
    },
    setPriceChangeAcknowledged: (state, action: PayloadAction<boolean>) => {
      if (!state.priceChangeSignature) return
      state.priceChangeAcknowledgedSignature = action.payload ? state.priceChangeSignature : null
    },
    clearPriceChangeAcknowledgement: (state) => {
      state.priceChangeSignature = null
      state.priceChangeAcknowledgedSignature = null
      state.priceChangeNotice = null
    },
  },
})

export function selectCheckoutPriceAcknowledgement(state: { checkoutUx: CheckoutUxState }) {
  const signature = state.checkoutUx.priceChangeSignature
  const acknowledgedSignature = state.checkoutUx.priceChangeAcknowledgedSignature
  return {
    signature,
    notice: state.checkoutUx.priceChangeNotice ?? [],
    required: isPriceChangeAcknowledgementRequired(signature, acknowledgedSignature),
    acknowledged: Boolean(signature) && signature === acknowledgedSignature,
  }
}

export const {
  setCheckoutPhase,
  setCheckoutPaymentMode,
  setCheckoutFulfilment,
  resetCheckoutFulfilment,
  setCheckoutInstructions,
  clearCheckoutInstructions,
  noteCheckoutPriceChange,
  setPriceChangeAcknowledged,
  clearPriceChangeAcknowledgement,
} = checkoutUxSlice.actions
export default checkoutUxSlice.reducer

type FulfilmentSessionScope = {
  userId: string | null | undefined
  restaurantId: string | null | undefined
}

type StateWithCheckoutUx = {
  checkoutUx?: CheckoutUxState
}

function scopeId(value: string | null | undefined): string | null {
  const trimmed = typeof value === "string" ? value.trim() : ""
  return trimmed ? trimmed : null
}

/**
 * Customer's explicit choice: Redux first, then the restoration mirror. Only Pickup is
 * remembered; choosing Delivery (the default) removes the stored entry.
 */
export const selectCheckoutFulfilmentForSession =
  (input: FulfilmentSessionScope & { fulfilment: Fulfilment }) =>
  (dispatch: Dispatch): void => {
    dispatch(setCheckoutFulfilment(input.fulfilment))
    const restaurantId = scopeId(input.restaurantId)
    const userId = scopeId(input.userId)
    if (!restaurantId) return
    if (input.fulfilment === "PICKUP" && userId) {
      writeCheckoutFulfilmentSession(restaurantId, userId)
    } else {
      clearCheckoutFulfilmentSession(restaurantId)
    }
  }

/**
 * New-document restoration of an explicit Pickup choice. Call only once restaurant
 * configuration has loaded: when Pickup is not enabled the stored choice is discarded and
 * Delivery stays. Restores only for the same customer and restaurant. Returns true when restored.
 */
export const restoreCheckoutFulfilmentForSession =
  (scope: FulfilmentSessionScope & { pickupEnabled: boolean }) =>
  (dispatch: Dispatch, getState: () => StateWithCheckoutUx): boolean => {
    const restaurantId = scopeId(scope.restaurantId)
    const userId = scopeId(scope.userId)
    if (!restaurantId || !userId) return false
    if (scope.pickupEnabled !== true) {
      clearCheckoutFulfilmentSession(restaurantId)
      return false
    }
    const stored = readCheckoutFulfilmentSession(restaurantId)
    if (!stored || stored.userId !== userId) return false
    if (getState().checkoutUx?.fulfilment === "PICKUP") return false
    dispatch(setCheckoutFulfilment("PICKUP"))
    return true
  }

/** Restaurant change, successful order, logout: back to Delivery and nothing to restore. */
export const resetCheckoutFulfilmentForSession =
  () =>
  (dispatch: Dispatch): void => {
    dispatch(resetCheckoutFulfilment())
    clearAllCheckoutFulfilmentSessions()
  }
